import React, { useContext, useMemo } from 'react'
import GeoContext from 'contexts/geoContext'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { isAvailableForPurchase } from 'selectors/offerPage/offerPageSelectors'
import Group from 'components/utils/Group'
import OfferPackagePartnershipLabel from 'components/Common/Labels/OfferPackagePartnershipLabel'
import FlashCancellationPolicies from 'components/Common/CancellationPolicies/Flash/FlashCancellationPolicies'
import FlashHotelCheapestOptionPriceDetailsVariant from './FlashHotelCheapestOptionPriceDetailsVariant'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import { generateLoyaltyPointsCalculatorHotelOptions, generateLoyaltyPointsCalculatorBundledFlightOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import BodyText from 'LuxKit/Typography/BodyText'

interface Props {
  offer: App.Offer
  checkIn?: string
  packageView: App.OfferPackageOptionView
  roomsBeforeSurcharge?: number
  previewMode: boolean
  detailsModalMode: 'room' | 'room-and-package'
  hasMultipleCancellationPolicies: boolean
  availableForPurchase?: boolean
}

function FlashHotelCheapestRateOptionVariant(props: Props) {
  const {
    offer,
    checkIn,
    packageView,
    roomsBeforeSurcharge,
    previewMode,
    detailsModalMode,
    hasMultipleCancellationPolicies,
    availableForPurchase,
  } = props

  const { partnership, currentRegionCode } = useContext(GeoContext)
  const {
    package: pkg,
  } = packageView

  const showPartnership = (packageView.hotelAvailable &&
    !!partnership?.hasEarn &&
    offer.canEarnPartnershipPoints)

  const showPrices = (packageView.capacityValid && packageView.hotelAvailable) || previewMode

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView, currentRegionCode }),
    generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
      packageView,
      skip: !offer.bundledWithFlightsOnly,
    }),
  ], [offer, packageView, currentRegionCode])

  return (
    <Group direction="vertical" gap={16} data-testid={pkg.uniqueKey}>
      <Group direction="vertical" gap={8} noDisplayWhenEmpty>
        {hasMultipleCancellationPolicies && (
          <BodyText variant="medium">
            <BodyText variant="medium" weight="bold" colour="highlight-secondary" as="span">Flexible cancellation</BodyText>{' '}
            options available
          </BodyText>
        )}
        {!hasMultipleCancellationPolicies && availableForPurchase && offer.property && pkg.roomRate && <FlashCancellationPolicies
          checkInDate={checkIn}
          propertyTimezoneOffset={offer.property.timezoneOffset}
          cancellationPolicy={pkg.roomRate.cancellationPolicy}
        />}
        {showPrices && <LoyaltyPointsEarnWithBreakdownModal
          calculationRequests={pointsEarnCalculationRequests}
          calculationType={!checkIn ? 'estimate' : 'exact'}
        />}
      </Group>
      <Group direction="vertical" gap={8}>
        {!!showPartnership && pkg.partnership && <OfferPackagePartnershipLabel
          offer={offer}
          price={packageView.hotelPrice}
          packagePartnership={pkg.partnership}
        />}
        {showPrices && <FlashHotelCheapestOptionPriceDetailsVariant
          packageView={packageView}
          roomsBeforeSurcharge={roomsBeforeSurcharge}
          detailsModalMode={detailsModalMode}
        />}
      </Group>
    </Group>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  checkIn: state.checkInDate,
  partnership: appState.geo.partnership,
  previewMode: appState.system.previewMode,
  availableForPurchase: isAvailableForPurchase(state, appState),
}), FlashHotelCheapestRateOptionVariant)
